import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import useUser from '@hooks/user/use-user';
import useUpdatePersonalInformation from '@hooks/user/use-update-personal-information';
import AccountSettingsSectionHeader from '@components/account-settings/account-settings-section-header/account-settings-section-header';
import PersonalInformationDisplay from './personal-information-display/personal-information-display';
import PersonalInformationEdit from './personal-information-edit/personal-information-edit';

const PersonalInformation: React.FC = () => {
  const { t } = useTranslation('common');
  const { user } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { mutateAsync: updatePersonalInformation, isLoading } = useUpdatePersonalInformation(user?.id);

  if (!user) {
    return null;
  }

  const handleSave = async ({
    firstname,
    lastname,
    username,
    bio,
  }: {
    firstname: string;
    lastname: string;
    username: string;
    bio: string;
  }) => {
    setApiError(null);
    try {
      await updatePersonalInformation({ firstName: firstname, lastName: lastname, username, mood: bio || ' ' });
      setIsEditing(false);
    } catch (error) {
      setApiError((error?.response?.data?.errors?.detail as string) ?? t('ALERT.GENERIC_ERROR'));
    }
  };

  return (
    <section>
      <AccountSettingsSectionHeader
        title={t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.PERSONAL_INFORMATION_TITLE', { ns: 'account-settings' })}
        onEditClick={() => {
          setIsEditing((prev) => !prev);
          setApiError(null);
        }}
        isEditing={isEditing}
        trackingProperty="personal_information"
        data-cy="account-settings-personal-information-edit-button"
      />

      {isEditing ? (
        <PersonalInformationEdit
          firstname={user.firstname}
          lastname={user.lastname}
          username={user.username}
          bio={user.mood}
          isPending={isLoading}
          onSave={handleSave}
          apiError={apiError}
          onErrorReset={() => setApiError(null)}
        />
      ) : (
        <PersonalInformationDisplay
          firstname={user.firstname}
          lastname={user.lastname}
          username={user.username}
          bio={user.mood}
        />
      )}
    </section>
  );
};

export default PersonalInformation;
